import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { toast, ToastContainer } from 'react-toastify';
import { BASE_URL } from '../../BASE_URL';
import Loader from '../../Components/Loader/Loader';
import './DocumentUpdate.css';
import EditIcon from '@mui/icons-material/Edit';

const DocumentUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [docData, setDocData] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedFrontImage, setSelectedFrontImage] = useState(null);
  const [selectedBackImage, setSelectedBackImage] = useState(null);
  const [showFrontInput, setShowFrontInput] = useState(false);
  const [showBackInput, setShowBackInput] = useState(false);

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  const docs = [
    { value: "Aadhaar card", label: "Aadhaar card" },
    { value: "Driving Licence", label: "Driving Licence" },
    { value: "Passport", label: "Passport" },
    { value: "Voter Id", label: "Voter Id" },
  ];

  useEffect(() => {
    fetchDocumentDetails();
  }, [id]);

  const fetchDocumentDetails = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/doc_verification`, {
        headers: {
          Authorization: localStorage.getItem('saptvidhiUserToken'),
        },
      });
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        const doc = data.data[0]; // Get first document
        setDocData(doc);
        setSelectedDoc({ value: doc.doc_type, label: doc.doc_type });
        setSelectedFrontImage(doc.front_doc_photo);
        setSelectedBackImage(doc.back_doc_photo);
      }
    } catch (error) {
      toast.error('Error fetching document details');
    }
  };

  const handleDocChange = (selectedOption) => {
    setSelectedDoc(selectedOption);
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select only image files (JPG, PNG)');
      return false;
    }
    return true;
  };

  const handleFrontImageChange = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFrontImage(reader.result);
        setShowFrontInput(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackImageChange = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedBackImage(reader.result);
        setShowBackInput(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDoc || !selectedFrontImage || !selectedBackImage) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('doc_type', selectedDoc.value);
      
      if (selectedFrontImage.startsWith('data:')) {
        formData.append('front_doc_photo', dataURItoBlob(selectedFrontImage));
      }
      
      if (selectedBackImage.startsWith('data:')) {
        formData.append('back_doc_photo', dataURItoBlob(selectedBackImage));
      }

      const response = await fetch(`${BASE_URL}/api/doc_verification/update?id=${id}`, {
        method: 'PUT',
        headers: {
          Authorization: localStorage.getItem('saptvidhiUserToken'),
        },
        body: formData,
      });

      if (response.ok) {
        toast.success('Document updated successfully');
        setTimeout(() => navigate('/documents'), 1000);
      } else {
        toast.error('Failed to update document');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  function dataURItoBlob(dataURI) {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  return (
    <div className="document-update-container">
      <div className="document-update-box">
        <h3 className="text-center mb-4">Update Document</h3>
        <div className="document-form">
          <div className="form-group mb-4">
            <h6>Document Type</h6>
            <Select
              value={selectedDoc}
              onChange={handleDocChange}
              options={docs}
              className="document-select"
            />
          </div>

          <div className="document-images-update">
            <div className="image-section">
              <h6 className="text-center">Front Photo</h6>
              <div className="image-upload-box">
                {selectedFrontImage && !showFrontInput ? (
                  <div className="preview-image-container">
                    <div className="preview-image mb-3">
                      <img src={selectedFrontImage} alt="Front" />
                    </div>
                    <button 
                      className="edit-image-btn"
                      onClick={() => {
                        setShowFrontInput(true);
                        setTimeout(() => {
                          if (frontInputRef.current) {
                            frontInputRef.current.accept = "image/png,image/jpeg,image/jpg";
                            frontInputRef.current.click();
                          }
                        }, 0);
                      }}
                    >
                      <EditIcon />
                    </button>
                  </div>
                ) : (
                  <div className="file-input-container">
                    <input
                      ref={frontInputRef}
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleFrontImageChange}
                      className="file-input"
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="select-file-btn"
                      onClick={() => frontInputRef.current.click()}
                    >
                      Select Image
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="image-section">
              <h6 className="text-center">Back Photo</h6>
              <div className="image-upload-box">
                {selectedBackImage && !showBackInput ? (
                  <div className="preview-image-container">
                    <div className="preview-image mb-3">
                      <img src={selectedBackImage} alt="Back" />
                    </div>
                    <button 
                      className="edit-image-btn"
                      onClick={() => {
                        setShowBackInput(true);
                        setTimeout(() => {
                          if (backInputRef.current) {
                            backInputRef.current.accept = "image/png,image/jpeg,image/jpg";
                            backInputRef.current.click();
                          }
                        }, 0);
                      }}
                    >
                      <EditIcon />
                    </button>
                  </div>
                ) : (
                  <div className="file-input-container">
                    <input
                      ref={backInputRef}
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={handleBackImageChange}
                      className="file-input"
                      style={{ display: 'none' }}
                    />
                    <button 
                      className="select-file-btn"
                      onClick={() => backInputRef.current.click()}
                    >
                      Select Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="submit-section">
            {loading ? (
              <button disabled className="update-submit-btn">
                Updating...
              </button>
            ) : (
              <button className="update-submit-btn" onClick={handleSubmit}>
                Update Document
              </button>
            )}
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default DocumentUpdate;